import { Search, User, X } from 'lucide-react'
import Image from 'next/image'
import { useRouter } from 'next/router'
import * as React from 'react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'

import userService from '@/services/user.service'

import type { UserProfile } from '@/types/user.type'

interface UserSearchProps {
  onUserSelect: (user: UserProfile) => void
  onUserRemove?: (userId: number) => void
  selectedUsers?: UserProfile[]
  placeholder?: string
  maxUsers?: number
  excludeUserIds?: number[]
  enableProfileNavigation?: boolean
}

const UserSearch: React.FC<UserSearchProps> = ({
  onUserSelect,
  onUserRemove,
  selectedUsers = [],
  placeholder = 'Search users...',
  maxUsers = 10,
  excludeUserIds = [],
  enableProfileNavigation = false,
}) => {
  const [searchQuery, setSearchQuery] = React.useState('')
  const [searchResults, setSearchResults] = React.useState<UserProfile[]>([])
  const [isSearching, setIsSearching] = React.useState(false)
  const [showResults, setShowResults] = React.useState(false)

  const router = useRouter()
  const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  // Search users
  const searchUsers = React.useCallback(
    async (query: string) => {
      if (!query.trim()) {
        setSearchResults([])
        setShowResults(false)
        return
      }

      setIsSearching(true)
      try {
        // Use actual user service to search users
        const searchResults = await userService.searchUsers({
          query: query.trim(),
        })

        // Filter out already selected users and excluded users
        const filteredUsers = searchResults
          .filter(
            (user: UserProfile) =>
              !selectedUsers.some(selected => selected.id === user.id) && !excludeUserIds.includes(user.id)
          )
          .slice(0, maxUsers)

        setSearchResults(filteredUsers)
        setShowResults(true)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to search users:', error)
        toast.error('Failed to search users')
        setSearchResults([])
        setShowResults(false)
      } finally {
        setIsSearching(false)
      }
    },
    [selectedUsers, excludeUserIds, maxUsers]
  )

  // Handle search input change
  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const query = e.target.value
      setSearchQuery(query)

      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }

      // Debounce search
      searchTimeoutRef.current = setTimeout(() => {
        searchUsers(query)
      }, 300)
    },
    [searchUsers]
  )

  // Handle user selection
  const handleUserSelect = React.useCallback(
    (user: UserProfile) => {
      onUserSelect(user)
      setSearchQuery('')
      setSearchResults([])
      setShowResults(false)
    },
    [onUserSelect]
  )

  // Handle user removal
  const handleUserRemove = React.useCallback(
    (userId: number) => {
      onUserRemove?.(userId)
    },
    [onUserRemove]
  )

  // Handle input focus
  const handleInputFocus = React.useCallback(() => {
    if (searchQuery.trim() && searchResults.length > 0) {
      setShowResults(true)
    }
  }, [searchQuery, searchResults.length])

  // Handle input blur
  const handleInputBlur = React.useCallback(() => {
    // Delay hiding results to allow for clicks
    setTimeout(() => {
      setShowResults(false)
    }, 200)
  }, [])

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  return (
    <div className="relative">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          value={searchQuery}
          onChange={handleSearchChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          className="pl-10 pr-4"
        />
        {isSearching && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Search Results */}
      {showResults && (searchResults.length > 0 || isSearching) && (
        <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
          {isSearching ? (
            <div className="p-4 text-center text-gray-500">
              <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-2" />
              Searching...
            </div>
          ) : searchResults.length === 0 ? (
            <div className="p-4 text-center text-gray-500">No users found</div>
          ) : (
            <div className="py-2">
              {searchResults.map(user => (
                <button
                  key={user.id}
                  onClick={() => {
                    if (enableProfileNavigation) {
                      // Clear search state
                      setSearchQuery('')
                      setSearchResults([])
                      setShowResults(false)
                      // Always use profileId for navigation
                      if (user.profileId) {
                        router.push(`/user/${user.profileId}`)
                      } else {
                        // Only fallback to user ID if profileId is truly not available
                        router.push(`/user/${user.id}`)
                      }
                    } else {
                      handleUserSelect(user)
                    }
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                    {user.avatar ? (
                      <Image
                        src={user.avatar}
                        alt={`${user.firstName} ${user.lastName}`}
                        width={32}
                        height={32}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <User className="w-4 h-4 text-gray-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {user.firstName} {user.lastName}
                    </div>
                    <div className="text-sm text-gray-500 truncate">{user.email}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Selected Users */}
      {selectedUsers.length > 0 && (
        <div className="mt-3 space-y-2">
          <div className="text-sm font-medium text-gray-700">Selected Users:</div>
          <div className="flex flex-wrap gap-2">
            {selectedUsers.map(user => (
              <Badge key={user.id} variant="secondary" className="flex items-center gap-1">
                <div className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center">
                  {user.avatar ? (
                    <Image
                      src={user.avatar}
                      alt={`${user.firstName} ${user.lastName}`}
                      width={16}
                      height={16}
                      className="w-4 h-4 rounded-full object-cover"
                    />
                  ) : (
                    <User className="w-2 h-2 text-gray-600" />
                  )}
                </div>
                <span className="text-xs">
                  {user.firstName} {user.lastName}
                </span>
                {onUserRemove && (
                  <button
                    onClick={() => handleUserRemove(user.id)}
                    className="ml-1 hover:text-red-500 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default UserSearch
